import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, Switch, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import type { CampaignCommissionTargetType, ICampaignCommissionConfig } from '../../redux/buzzbridge/types';
import { getUserContests } from '../../redux/contest/actions';
import type { RootState } from '../../redux/root-reducer';
import { isSuperAdminSelector } from '../../selectors/selectors';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useDispatchPromise } from '../../utils/AppUtils';
import {
    defaultContestDisplayPoolCents,
    DEFAULT_EXTERNAL_DISPLAY_POOL_CENTS,
    formatCommissionPoolDollars,
} from '../../utils/commissionCampaign';
import { BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER_HIGHEST } from './brandDashboardTokens';

const labelSx = {
    fontSize: 12,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    color: 'rgba(255,255,255,0.55)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    mb: 1,
};

const inputSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
        color: '#fff',
        borderRadius: '12px',
        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(255,201,101,0.35)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,201,101,0.55)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.55)' },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND_LUXE_PRIMARY },
    '& .MuiOutlinedInput-input': { color: '#fff' },
    '& .MuiSelect-icon': { color: BRAND_LUXE_PRIMARY },
};

type ContestOption = { id: string; label: string };

function mapContestOptions(rows: Array<{ _id?: string; title?: string; slug?: string }> | undefined): ContestOption[] {
    const seen = new Set<string>();
    const out: ContestOption[] = [];
    for (const c of rows || []) {
        if (!c?._id || seen.has(c._id)) continue;
        seen.add(c._id);
        out.push({ id: c._id, label: c.title || c.slug || c._id });
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
}

interface Props {
    value: ICampaignCommissionConfig;
    influencerReadme: string;
    onChange: (next: ICampaignCommissionConfig) => void;
    onReadmeChange: (readme: string) => void;
    brandSurface?: string;
    /** Optional title when contest id is set but not yet in search results */
    linkedContestLabel?: string;
}

export function CommissionCampaignFields({
    value,
    influencerReadme,
    onChange,
    onReadmeChange,
    brandSurface = 'rgba(255,255,255,0.04)',
    linkedContestLabel,
}: Props) {
    const dispatchPromise = useDispatchPromise();
    const isSuperAdmin = useSelector(isSuperAdminSelector);
    const userContests = useSelector((s: RootState) => s.contest?.userContests ?? []);

    const [contestSearchInput, setContestSearchInput] = useState('');
    const [contestOptions, setContestOptions] = useState<ContestOption[]>([]);
    const [contestLoading, setContestLoading] = useState(false);

    const scopeAll = Boolean(isSuperAdmin);

    const fetchContests = useCallback(
        async (search: string) => {
            setContestLoading(true);
            try {
                const data = await dispatchPromise(
                    getUserContests({
                        limit: 50,
                        page: 1,
                        ...(scopeAll ? { scope: 'all' as const } : {}),
                        ...(search.trim() ? { search: search.trim() } : {}),
                    }),
                );
                const rows = Array.isArray(data) ? data : (data as any)?.data ?? [];
                setContestOptions(mapContestOptions(rows));
            } catch {
                setContestOptions([]);
            } finally {
                setContestLoading(false);
            }
        },
        [dispatchPromise, scopeAll],
    );

    useEffect(() => {
        if (value.target_type !== 'contest') return;
        const t = window.setTimeout(
            () => {
                void fetchContests(contestSearchInput);
            },
            contestSearchInput ? 300 : 0,
        );
        return () => window.clearTimeout(t);
    }, [contestSearchInput, value.target_type, fetchContests]);

    /** Keep selected contest visible even if not in current search page */
    const selectedOption = useMemo(() => {
        if (!value.contest) return null;
        const fromOptions = contestOptions.find(o => o.id === value.contest);
        if (fromOptions) return fromOptions;
        const fromStore = (userContests || []).find(c => c._id === value.contest);
        if (fromStore) {
            return { id: fromStore._id, label: fromStore.title || fromStore.slug || fromStore._id };
        }
        return {
            id: value.contest,
            label: linkedContestLabel || value.contest,
        };
    }, [value.contest, contestOptions, userContests, linkedContestLabel]);

    useEffect(() => {
        if (!selectedOption?.label) return;
        setContestSearchInput(prev => (prev.trim() ? prev : selectedOption.label));
    }, [selectedOption?.id, selectedOption?.label]);

    const setTarget = (target_type: CampaignCommissionTargetType) => {
        if (target_type === 'external') {
            onChange({
                target_type: 'external',
                rate: value.rate || 15,
                requires_approval: value.requires_approval !== false,
                display_pool_cents: value.display_pool_cents || DEFAULT_EXTERNAL_DISPLAY_POOL_CENTS,
                external: value.external || {
                    product_name: '',
                    product_url: '',
                    fulfillment: 'physical',
                },
            });
            return;
        }
        onChange({
            target_type: 'contest',
            rate: value.rate || 20,
            requires_approval: value.requires_approval !== false,
            contest: value.contest || '',
            display_pool_cents: value.display_pool_cents || defaultContestDisplayPoolCents(value.rate || 20),
        });
    };

    const poolCents =
        value.display_pool_cents ??
        (value.target_type === 'external'
            ? DEFAULT_EXTERNAL_DISPLAY_POOL_CENTS
            : defaultContestDisplayPoolCents(Number(value.rate) || 0));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ bgcolor: brandSurface, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography sx={labelSx}>Influencer readme</Typography>
                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', mb: 1.5, lineHeight: 1.5 }}>
                    Primary “what you’ll do” note. Mention the rate and that earnings clear once after the campaign
                    ends.
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    value={influencerReadme}
                    onChange={e => onReadmeChange(e.target.value)}
                    placeholder="Post about the contest, drive people to submit with your link. You earn 20% of attributed submission fees…"
                    sx={inputSx}
                />
            </Box>

            <Box sx={{ bgcolor: brandSurface, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography sx={labelSx}>Commission target</Typography>
                <Box
                    sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        p: 2,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(255,201,101,0.06)',
                        border: '1px solid rgba(255,201,101,0.22)',
                    }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff' }}>
                            Require approval
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', mt: 0.5, lineHeight: 1.45 }}>
                            On: influencers apply and you approve before they get a promo code. Off: auto-approve on
                            apply.
                        </Typography>
                    </Box>
                    <Switch
                        checked={value.requires_approval !== false}
                        onChange={(_, checked) => onChange({ ...value, requires_approval: checked })}
                        inputProps={{ 'aria-label': 'Require approval' }}
                        sx={{
                            flexShrink: 0,
                            '& .MuiSwitch-switchBase.Mui-checked': {
                                color: BRAND_LUXE_PRIMARY,
                                '& + .MuiSwitch-track': { backgroundColor: 'rgba(255,201,101,0.5)', opacity: 1 },
                            },
                            '& .MuiSwitch-track': { opacity: 1, backgroundColor: '#484847' },
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {(
                        [
                            { id: 'contest', label: 'Contest fees' },
                            { id: 'external', label: 'External product' },
                        ] as const
                    ).map(opt => (
                        <Box
                            key={opt.id}
                            component="button"
                            type="button"
                            onClick={() => setTarget(opt.id)}
                            sx={{
                                flex: 1,
                                py: 1.25,
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor:
                                    value.target_type === opt.id ? 'rgba(255,201,101,0.55)' : 'rgba(255,255,255,0.1)',
                                bgcolor:
                                    value.target_type === opt.id ? 'rgba(255,201,101,0.12)' : 'rgba(255,255,255,0.03)',
                                color: value.target_type === opt.id ? '#ffc965' : 'rgba(255,255,255,0.65)',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                cursor: 'pointer',
                            }}>
                            {opt.label}
                        </Box>
                    ))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <TextField
                        label="Commission rate (%)"
                        type="number"
                        value={value.rate ?? ''}
                        onChange={e => {
                            const rate = parseFloat(e.target.value);
                            const nextRate = Number.isFinite(rate) ? rate : 0;
                            onChange({
                                ...value,
                                rate: nextRate,
                                display_pool_cents:
                                    value.target_type === 'contest'
                                        ? defaultContestDisplayPoolCents(nextRate)
                                        : value.display_pool_cents || DEFAULT_EXTERNAL_DISPLAY_POOL_CENTS,
                            });
                        }}
                        inputProps={{ min: 1, max: 100, step: 1 }}
                        sx={inputSx}
                    />
                    <TextField
                        label={value.target_type === 'external' ? 'Commission pool ($)' : 'Display pool ($)'}
                        type="number"
                        value={poolCents / 100}
                        onChange={e => {
                            const dollars = parseFloat(e.target.value);
                            onChange({
                                ...value,
                                display_pool_cents: Number.isFinite(dollars) ? Math.round(dollars * 100) : poolCents,
                            });
                        }}
                        helperText={
                            value.target_type === 'external'
                                ? `Locked in escrow before publishing — funds creator payouts from sales (${formatCommissionPoolDollars(poolCents)})`
                                : `Marketing estimate shown to creators (${formatCommissionPoolDollars(poolCents)})`
                        }
                        FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)' } }}
                        sx={inputSx}
                    />
                </Box>

                {value.target_type === 'contest' ? (
                    <Box sx={{ mt: 2 }}>
                        <Typography sx={{ ...labelSx, mb: 1 }}>
                            Linked contest{' '}
                            <Typography component="span" sx={{ color: '#f87171', fontFamily: 'inherit', fontSize: 'inherit' }}>
                                *
                            </Typography>
                        </Typography>
                        <Autocomplete
                            options={contestOptions}
                            loading={contestLoading}
                            value={selectedOption}
                            inputValue={contestSearchInput}
                            onInputChange={(_, next, reason) => {
                                if (reason === 'reset') return;
                                setContestSearchInput(next);
                            }}
                            onChange={(_, opt) => {
                                onChange({ ...value, contest: opt?.id || '' });
                                if (opt) setContestSearchInput(opt.label);
                            }}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            getOptionLabel={o => o.label}
                            filterOptions={x => x}
                            noOptionsText={
                                contestLoading
                                    ? 'Searching…'
                                    : contestSearchInput.trim()
                                    ? 'No contests match your search'
                                    : 'No contests found — create one first'
                            }
                            sx={{
                                '& .MuiAutocomplete-popupIndicator': { color: BRAND_LUXE_PRIMARY },
                                '& .MuiAutocomplete-clearIndicator': { color: 'rgba(255,255,255,0.45)' },
                            }}
                            slotProps={{
                                paper: {
                                    sx: {
                                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                        color: 'rgba(255,255,255,0.9)',
                                        backgroundImage: 'none',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        '& .MuiAutocomplete-option': {
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 14,
                                        },
                                        '& .MuiAutocomplete-option.Mui-focused': {
                                            bgcolor: 'rgba(255,201,101,0.12)',
                                        },
                                    },
                                },
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    placeholder="Search contests by title…"
                                    sx={inputSx}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {contestLoading ? (
                                                    <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        {!value.contest && (
                            <Typography sx={{ mt: 0.75, fontSize: 12, color: '#f87171' }}>
                                A contest must be linked before you can continue.
                            </Typography>
                        )}
                        <Typography sx={{ mt: 0.75, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                            {isSuperAdmin
                                ? 'Super admin: search all contests.'
                                : 'Only contests you created (server-filtered).'}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Product name"
                            value={value.external?.product_name || ''}
                            onChange={e =>
                                onChange({
                                    ...value,
                                    external: { ...value.external, product_name: e.target.value },
                                })
                            }
                            sx={inputSx}
                        />
                        <TextField
                            fullWidth
                            label="Product URL"
                            value={value.external?.product_url || ''}
                            onChange={e =>
                                onChange({
                                    ...value,
                                    external: { ...value.external, product_url: e.target.value },
                                })
                            }
                            sx={inputSx}
                        />
                        <TextField
                            fullWidth
                            label="Store name"
                            value={value.external?.store_name || ''}
                            onChange={e =>
                                onChange({
                                    ...value,
                                    external: { ...value.external, store_name: e.target.value },
                                })
                            }
                            sx={inputSx}
                        />
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Product description"
                            value={value.external?.product_description || ''}
                            onChange={e =>
                                onChange({
                                    ...value,
                                    external: { ...value.external, product_description: e.target.value },
                                })
                            }
                            sx={inputSx}
                        />
                    </Box>
                )}

                <Typography sx={{ mt: 2, fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
                    {value.target_type === 'external'
                        ? 'Commission pool (and base pay if set) must be locked in escrow before publishing. Creator earnings are deducted from escrow as sales are reported.'
                        : 'Contest commission requires no upfront escrow — unless base pay is set, which must be locked before publishing. Commission earnings come from contest entry fees.'}
                </Typography>
            </Box>
        </Box>
    );
}
